'use strict'
import { useCallback } from 'react'

import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { hasErrorClass } from '../extra/common'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import { shallowEqual } from 'react-redux'

/*==================================
 定数定義
 ==================================*/
const COUNT_SETTING_OPTIONS = [
  {
    value: '含める',
    label: '人数に含める'
  },
  {
    value: '含めない',
    label: '人数に含めない'
  }
]

/*==================================
 カスタムフック
 ==================================*/
/**
 * 休職者の設定に関するロジックを管理するカスタムフック
 *
 * @returns {{
 *   employeeOnLeaveTarget: string
 *   fiscalYearErrors: object
 *   handleChangeEmployeeOnLeave: (event: { target: { value: string } }) => void
 * }}
 */
const useEmployeeOnLeaveLogic = () => {
  const dispatch = useEmployeeCountDispatch()

  const { employeeOnLeaveTarget, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]

    // employeeCountSettingは配列なので、0番目の要素にアクセス
    const employeeCountSetting = fiscalYearSettings?.employeeCountSetting?.[0] || {}

    return {
      employeeOnLeaveTarget: employeeCountSetting.employeeOnLeaveTarget,
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  /**
   * 休職者設定の選択変更ハンドラ
   *
   * @param {(event: { target: { value: string } }) => void} e - イベントオブジェクト => 選択された値 ('含める' or '含めない')
   * @returns {void}
   */
  const handleChangeEmployeeOnLeave = useCallback(
    (e) => {
      const value = e?.target?.value ?? ''
      dispatch('CHANGE_EMPLOYEE_ON_LEAVE', { employeeOnLeaveTarget: value })
    },
    [dispatch]
  )

  return {
    employeeOnLeaveTarget,
    fiscalYearErrors,
    handleChangeEmployeeOnLeave
  }
}

/*==================================
 コンポーネント
 ==================================*/
/**
 * 休職者の設定セクションをレンダリングするコンポーネント。
 *
 * @returns {React.ReactElement}
 * @component
 */
const EmployeeOnLeaveTargetSetting = () => {
  const { employeeOnLeaveTarget, fiscalYearErrors, handleChangeEmployeeOnLeave } = useEmployeeOnLeaveLogic()

  const empOnLeaveSelectClasses = ['w-[400px]'].join(' ')

  return (
    <>
      <div className="text-body-2-bold grow mb-3 ">■休職者の設定</div>
      <div className="flex form-control items-center">
        <span className="pr-2">休職中の従業員を</span>
        <ChrofyCustomSelect
          id="employee-on-leave"
          name="employeeOnLeaveTarget"
          value={employeeOnLeaveTarget}
          onChange={handleChangeEmployeeOnLeave}
          className={empOnLeaveSelectClasses}
          options={COUNT_SETTING_OPTIONS}
          disabled={false}
          isError={hasErrorClass('employeeOnLeaveTarget', fiscalYearErrors)}
          hasNoSelect={false}
        />
      </div>
      <ShowErrors clientErrors={fiscalYearErrors} fieldName="employeeOnLeaveTargetSetting" />
    </>
  )
}

export default EmployeeOnLeaveTargetSetting
